import React, { useCallback, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { useAuth } from '../../../contexts/AuthContext';
import {
  exceptionActorService,
  ExceptionActor,
} from '../../../services/exception-actor.service';
import {
  SectionHeader, TableShell, Th, Row, Td, LoadingState, EmptyState, Input, Button,
} from '../../ui';

/**
 * 처리자 관리 (IPT-613).
 *
 * DLP·NAC·유해차단 예외처리가 이 목록 하나를 공유한다. 담당자가 바뀌면 코드를 고치는 대신
 * 여기서 추가·삭제한다. '사용'을 끄면 신규 선택에서만 빠지고, 이미 저장된 값은 그대로 남는다
 * — 지난 이력을 바꾸지 않기 위해서다.
 */
const ExceptionActorSection: React.FC = () => {
  const { user } = useAuth();
  const isAdminOrEditor = user?.role === 'admin' || user?.role === 'editor';

  const [actors, setActors] = useState<ExceptionActor[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [newName, setNewName] = useState('');
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setIsLoading(true);
    try {
      // 관리 화면에서는 '사용 안 함'도 보여야 한다
      setActors(await exceptionActorService.getAll(true));
    } catch {
      setError('처리자 목록을 불러오지 못했습니다.');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  /** 서버가 이유를 담아 보내면(중복 이름 등) 그대로 보여준다 */
  const showError = (e: unknown, fallback: string) => {
    const message = (e as { response?: { data?: { message?: string } } })?.response?.data?.message;
    setError(message || fallback);
  };

  const handleCreate = async () => {
    const name = newName.trim();
    if (!name) return;

    setError(null);
    try {
      await exceptionActorService.create({ name });
      setNewName('');
      toast.success(`${name} 추가됨`);
      await load();
    } catch (e) {
      showError(e, '처리자를 추가하지 못했습니다.');
    }
  };

  const handleToggleActive = async (actor: ExceptionActor) => {
    setError(null);
    try {
      await exceptionActorService.update(actor.id, { isActive: !actor.isActive });
      await load();
    } catch (e) {
      showError(e, '사용 여부를 바꾸지 못했습니다.');
    }
  };

  const handleDelete = async (actor: ExceptionActor) => {
    if (!window.confirm(`'${actor.name}'을(를) 삭제하시겠습니까?\n이미 등록된 예외처리의 값은 그대로 남습니다.`)) {
      return;
    }

    setError(null);
    try {
      await exceptionActorService.remove(actor.id);
      toast.success(`${actor.name} 삭제됨`);
      await load();
    } catch (e) {
      showError(e, '처리자를 삭제하지 못했습니다.');
    }
  };

  return (
    <div>
      <SectionHeader
        title="처리자 관리"
        total={actors.length}
        description="DLP · NAC · 유해차단 예외처리에서 함께 쓰는 처리자 목록"
      />

      {error && (
        <div
          role="alert"
          className="mb-4 rounded-lg border border-destructive/40 bg-destructive/10 px-4 py-2.5 text-sm text-destructive"
          style={{ wordBreak: 'keep-all' }}
        >
          {error}
        </div>
      )}

      {isAdminOrEditor && (
        <div className="mb-5 flex items-center gap-2">
          <Input
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleCreate();
            }}
            placeholder="처리자 이름"
            className="w-[220px]"
          />
          <Button type="button" onClick={handleCreate}>추가</Button>
        </div>
      )}

      {isLoading ? (
        <LoadingState />
      ) : actors.length === 0 ? (
        <EmptyState text="등록된 처리자가 없습니다." />
      ) : (
        <TableShell
          headers={
            <>
              <Th>이름</Th>
              <Th>사용</Th>
              {isAdminOrEditor && <Th>관리</Th>}
            </>
          }
        >
          {actors.map((actor, idx) => (
            <Row key={actor.id} idx={idx}>
              <Td className={actor.isActive ? 'font-medium' : 'text-muted-foreground line-through'}>
                {actor.name}
              </Td>
              <Td>
                <input
                  type="checkbox"
                  checked={actor.isActive}
                  disabled={!isAdminOrEditor}
                  onChange={() => handleToggleActive(actor)}
                  aria-label={`${actor.name} 사용`}
                />
              </Td>
              {isAdminOrEditor && (
                <Td className="whitespace-nowrap">
                  <button
                    type="button"
                    onClick={() => handleDelete(actor)}
                    className="rounded border border-destructive/40 px-2 py-0.5 text-xs text-destructive transition-colors hover:bg-destructive/10"
                  >
                    삭제
                  </button>
                </Td>
              )}
            </Row>
          ))}
        </TableShell>
      )}

      <p className="mt-4 text-xs text-muted-foreground" style={{ wordBreak: 'keep-all' }}>
        ‘사용’을 끄면 새로 등록할 때 고를 수 없게 되고, 이미 저장된 예외처리의 처리자 값은 그대로 남습니다.
        엑셀 업로드도 이 목록을 기준으로 검사합니다.
      </p>
    </div>
  );
};

export default ExceptionActorSection;
